// Single source of truth for how instruments are displayed across the whole app
// (Watchlist, Portfolio, Trade, search). Pure — no React, no side effects.
//
// Three display forms, one per surface-role:
//   baseSymbol(sym, category?)  -> circle chip / ticker preview. BASE asset only.
//       "X:BTCUSD" -> "BTC"   "X:BTCAUD" -> "BTC"   "C:EURUSD" -> "EUR"
//       "C:USDJPY" -> "USD"   "AAPL" -> "AAPL"
//   pairSymbol(sym)             -> Trade asset-wheel label. Prefix stripped, pair kept.
//       "X:BTCUSD" -> "BTCUSD"   "C:USDJPY" -> "USDJPY"   "AAPL" -> "AAPL"
//   instrumentName({name,symbol,category}) -> title line. Server name, cleaned +
//       legal-suffix-trimmed + title-cased; falls back to the base symbol.
//
// The long human names ("Bitcoin - United States Dollar") only exist when the
// server supplies them — the formatter cannot synthesize them from a symbol.

// Known quote currencies (fiat + common crypto quotes), longest-first so "USDT"
// matches before "USD". Used to split a concatenated pair into base/quote.
const QUOTE_CURRENCIES = [
  "USDT", "USDC", "TUSD", "BUSD",
  "USD", "EUR", "JPY", "GBP", "AUD", "CAD", "CHF", "NZD",
  "CNY", "CNH", "HKD", "SGD", "SEK", "NOK", "DKK", "ZAR",
  "MXN", "INR", "RUB", "BRL", "TRY", "KRW", "PLN", "THB",
  "IDR", "HUF", "CZK", "ILS", "CLP", "PHP", "AED", "SAR",
  "COP", "MYR", "RON", "BTC", "ETH",
].sort((a, b) => b.length - a.length);

// Polygon exchange prefixes: "X:" crypto, "C:" forex.
const PREFIX_RE = /^[A-Za-z]:/;

export function stripExchangePrefix(sym) {
  if (!sym || typeof sym !== "string") return sym || "";
  return sym.trim().replace(PREFIX_RE, "");
}

// Split a concatenated pair ("BTCUSD") into { base, quote }. quote is null when
// no known quote suffix is found (treat the whole thing as the base).
function splitPair(pair) {
  const s = String(pair || "").toUpperCase();
  for (const q of QUOTE_CURRENCIES) {
    if (s.length > q.length && s.endsWith(q)) {
      return { base: s.slice(0, -q.length), quote: q };
    }
  }
  return { base: s, quote: null };
}

// Is this a tradable pair (crypto/forex) rather than a single equity? A symbol is
// only ever split when we KNOW it's a pair (exchange prefix present, or an
// explicit crypto/forex category) — so equities are never mis-split.
function looksLikePair(sym, category) {
  if (PREFIX_RE.test(String(sym || "").trim())) return true;
  const c = String(category || "").toLowerCase();
  return c === "crypto" || c === "forex";
}

// Circle chip / ticker preview — the BASE asset only.
export function baseSymbol(sym, category) {
  if (!sym || typeof sym !== "string") return sym || "";
  const stripped = stripExchangePrefix(sym);
  if (looksLikePair(sym, category)) return splitPair(stripped).base;
  return stripped;
}

// { base, quote, isPair } — for callers that still want both halves.
export function symbolParts(sym, category) {
  const stripped = stripExchangePrefix(sym);
  if (looksLikePair(sym, category)) {
    const { base, quote } = splitPair(stripped);
    return { base, quote, isPair: quote != null };
  }
  return { base: stripped, quote: null, isPair: false };
}

// Trade asset-wheel — prefix stripped, pair preserved ("BTCUSD" / "USDJPY").
export function pairSymbol(sym) {
  return stripExchangePrefix(sym);
}

// ---- name normalization ----

const SMALL_WORDS = new Set([
  "of", "the", "and", "a", "an", "in", "on", "for", "to", "vs", "de", "du", "van",
]);

// Title-case an instrument/currency name WITHOUT mangling acronyms. Only words
// that are entirely lowercase get capitalized ("dollar" -> "Dollar"); anything
// carrying existing caps is left intact ("SPDR", "S&P", "ETF", "iShares", "USD").
export function titleCaseInstrument(name) {
  if (!name || typeof name !== "string") return name || "";
  return name
    .split(" ")
    .map((word, i) => {
      if (word === word.toLowerCase() && /[a-z]/.test(word)) {
        if (i !== 0 && SMALL_WORDS.has(word)) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(" ");
}

// Legal-entity suffix tokens — cut the name right after the first one so
// "Alphabet Inc. Class C Capital Stock" -> "Alphabet Inc.". Matched as whole
// words (normalized without trailing . or ,) so "Incyte" is never mistaken.
const LEGAL_SUFFIXES = new Set([
  "inc", "incorporated", "corp", "corporation", "co", "company",
  "ltd", "limited", "llc", "lp", "llp", "plc", "ag", "sa", "nv", "se",
]);

export function shortenLegalName(name) {
  if (!name || typeof name !== "string") return name || "";
  const words = name.split(" ");
  for (let i = 0; i < words.length; i++) {
    const norm = words[i].replace(/[.,]+$/g, "").toLowerCase();
    if (LEGAL_SUFFIXES.has(norm)) {
      return words.slice(0, i + 1).join(" ").trim();
    }
  }
  return name;
}

// Drop any parenthetical chunk ("Foo (SPY)" -> "Foo").
function stripParenthetical(name) {
  return String(name || "").replace(/\s*\(.*?\)\s*/g, " ").trim();
}

// The title line used everywhere. Prefer the server-provided name (parenthetical
// + legal-suffix trimmed, title-cased); fall back to the base symbol.
export function instrumentName({ name, symbol, category } = {}) {
  let raw = typeof name === "string" ? name.trim() : "";
  // A pair sometimes arrives with its exchange-prefixed symbol AS the "name"
  // (forex holdings store company_name = "C:EURUSD", for example). That's not a
  // human name — strip the prefix so a C:/X: label never reaches the UI, leaving
  // the pair (e.g. "EURUSD"). Real names never start with a single-letter
  // exchange prefix, so this is a no-op for them.
  if (raw && PREFIX_RE.test(raw)) {
    raw = stripExchangePrefix(raw);
  }
  if (raw) {
    return titleCaseInstrument(shortenLegalName(stripParenthetical(raw)));
  }
  return baseSymbol(symbol, category);
}
